import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowRight,
  Zap,
  Target,
  TrendingUp,
  ClipboardList,
  BarChart3,
  CheckCircle2,
  Clock,
  Users,
  DollarSign,
  Shield,
  Lightbulb,
  History
} from 'lucide-react';

const CatalystStart = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Target,
      title: "COMPANEZE Score",
      description: "9-dimension business health analysis covering Customer, Operations, Monetization, and more"
    },
    {
      icon: ClipboardList,
      title: "Determinations",
      description: "AI-powered identification of critical issues, high-priority fixes, and growth blockers"
    },
    {
      icon: TrendingUp,
      title: "90-Day Sprint",
      description: "Week-by-week action plan with specific deliverables and metrics"
    },
    {
      icon: BarChart3,
      title: "12-Month Roadmap",
      description: "Quarterly milestones aligned to your business goals"
    }
  ];

  const scoreLabels = [
    { letter: "C", name: "Customer", color: "bg-blue-500" },
    { letter: "O", name: "Operations", color: "bg-green-500" },
    { letter: "M", name: "Monetization", color: "bg-amber-500" },
    { letter: "P", name: "Promotion", color: "bg-purple-500" },
    { letter: "A", name: "Automation", color: "bg-cyan-500" },
    { letter: "N", name: "Navigation", color: "bg-orange-500" },
    { letter: "E", name: "Expansion", color: "bg-pink-500" },
    { letter: "Z", name: "Zero Waste", color: "bg-indigo-500" },
    { letter: "E", name: "Exit Value", color: "bg-red-500" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-900 text-white py-6 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                  <span className="text-blue-900 font-bold text-lg">Cz</span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold">Catalyst</h1>
                  <p className="text-blue-200 text-sm">Business Optimization Engine</p>
                </div>
              </a>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/catalyst/history')}
              className="text-white border-white hover:bg-white/10"
            >
              <History className="w-4 h-4 mr-2" />
              History
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-blue-900 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-6 bg-amber-500 text-white hover:bg-amber-600">
            <Zap className="w-3 h-3 mr-1" />
            AI-Powered Business Analysis
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
            Know Exactly What to Fix
            <span className="block text-amber-400">Before You Scale</span>
          </h1>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Answer 10 questions about your business. Receive a complete optimization program 
            with scores, determinations, and a prioritized action plan.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/catalyst/wizard')}
              className="bg-amber-500 hover:bg-amber-600 text-blue-900 font-semibold text-lg px-8 py-6"
              data-testid="catalyst-start-btn"
            >
              Full Analysis - $199
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/catalyst/wizard')}
              className="border-white text-white hover:bg-white/10 text-lg px-8 py-6"
              data-testid="catalyst-lite-btn"
            >
              Single Problem - $49
            </Button>
          </div>
          <p className="mt-4 text-blue-200 text-sm">
            <Clock className="w-4 h-4 inline mr-1" />
            Takes about 5 minutes to complete
          </p>
        </div>
      </section>

      {/* COMPANEZE Score Breakdown */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">The COMPANEZE Framework</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              9 dimensions that determine your business readiness for growth
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {scoreLabels.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg border"
              >
                <div className={`w-8 h-8 ${item.color} rounded-lg flex items-center justify-center`}>
                  <span className="text-white font-bold text-sm">{item.letter}</span>
                </div>
                <span className="text-gray-700 font-medium">{item.name}</span>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-blue-50 rounded-xl border border-blue-200">
              <span className="text-blue-900 font-semibold">Combined:</span>
              <span className="text-2xl font-bold text-blue-900">CBIS</span>
              <span className="text-gray-600">(Companeze Business Index Score)</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What You'll Receive</h2>
            <p className="text-lg text-gray-600">
              A complete business optimization program, not just a report
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <Card key={idx} className="border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all">
                  <CardHeader>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-900 rounded-xl flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{feature.title}</CardTitle>
                        <CardDescription>{feature.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How It Works</h2>
          </div>
          
          <div className="space-y-6">
            {[
              { step: 1, title: "Answer 10 Questions", desc: "About your offering, customers, revenue, systems, and goals" },
              { step: 2, title: "Get Scored", desc: "Our algorithm analyzes your answers across 9 business dimensions" },
              { step: 3, title: "Receive Determinations", desc: "AI identifies critical issues and prioritizes them by severity" },
              { step: 4, title: "Follow Your Program", desc: "Execute your personalized 90-day sprint and 12-month roadmap" }
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-900 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">{item.step}</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Lightbulb className="w-12 h-12 text-amber-400 mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">
            Stop Guessing. Start Optimizing.
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join hundreds of businesses that have used Catalyst to identify 
            and fix their growth blockers.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/catalyst/wizard')}
            className="bg-amber-500 hover:bg-amber-600 text-blue-900 font-semibold text-lg px-8 py-6"
          >
            Start Your Assessment
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm">
            © 2025 Companeze Catalyst. Part of the Companeze BLDR platform.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CatalystStart;
